import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { ListUIStore } from '@modules/order/stores/view/List/ListUIStore'
import difference from 'lodash/difference'
import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import reject from 'lodash/reject'
import uniq from 'lodash/uniq'
import { useObserver } from 'mobx-react-lite'
import React, { FC, useMemo } from 'react'
import ChipListSkeleton from '../../../CellLoaders/ChipListSkeleton'
import { ChipSelect, RMClusterChip } from '../../components/RMClusterChip'
import * as css from './RMClusters.css'
import {
  getExcludedClusters,
  useProductStatus
} from '@components/UI-Components/ProductCard/ProductStatusHook'

const RMClusters: FC<CellItem> = ({ rowData, column }) =>
  useObserver(function useHook() {
    const {
      uiStore: {
        dataStore: { summary },
        isOrderEditable
      }
    } = useOrderContext()
    const { uiStore } = useViewContext()
    const { bookUnbookDoors } = uiStore as ListUIStore
    const { quantity: { doorProducts = {} } = {} } = rowData || {}

    const clusters = useMemo(
      () => reject(uniq(map(summary.doorOrders, 'door.cluster.name')), isEmpty),
      [summary?.doorOrders]
    )

    const excludedClusters = useMemo(
      () => getExcludedClusters(rowData?.excludedInDoors, summary?.doorOrders),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [summary.doorOrders, rowData?.excludedInDoors]
    )

    const clusterColorMap = useMemo(
      () =>
        reduce(
          summary?.doorOrders,
          (acc, doorOrder) => ({
            ...acc,
            [doorOrder.door.cluster.name]: doorOrder.door.cluster.color
          }),
          {}
        ),
      [summary?.doorOrders]
    )
    const selectedClusters = useMemo(
      () =>
        reject(
          uniq(
            map(
              filter(summary?.doorOrders, ({ id }) => doorProducts[id]),
              'door.cluster.name'
            )
          ),
          isEmpty
        ),
      [summary?.doorOrders, doorProducts]
    )

    const itemTemplate = useMemo(
      () => RMClusterChip({ colorMap: clusterColorMap }),
      [clusterColorMap]
    )
    const isProductActive: boolean = useProductStatus(rowData)

    if (!rowData) {
      return (
        <div>
          <ChipListSkeleton count={3} />
        </div>
      )
    }
    return (
      <div className={css['chip-list-container']}>
        <ChipSelect
          disabledOptions={excludedClusters}
          options={clusters}
          disabled={!rowData?.original?.active || !isOrderEditable || !isProductActive}
          selected={selectedClusters}
          itemTemplate={itemTemplate}
          onSelect={({ selected }) => {
            const addedClusters = new Set(difference(selected, selectedClusters))
            if (addedClusters.size) {
              bookUnbookDoors(
                summary.clientOrder.clientOrderId,
                rowData.id,
                map(
                  filter(summary.doorOrders, (doorOrder: any) =>
                    addedClusters.has(doorOrder?.door?.cluster.name)
                  ),
                  'id'
                ),
                true
              )
            } else {
              const removedClusters = new Set(difference(selectedClusters, selected))
              bookUnbookDoors(
                summary.clientOrder.clientOrderId,
                rowData.id,
                map(
                  filter(summary.doorOrders, doorOrder =>
                    removedClusters.has(doorOrder?.door?.cluster.name)
                  ),
                  'id'
                ),
                false
              )
            }
          }}
        />
      </div>
    )
  })

export default RMClusters
